import type React from 'react';
import { useEffect, useRef } from 'react';

/**
 * Focuses the first actionable descendant of the returned ref's element once it mounts.
 *
 * These filter editors are only mounted while their containing popover is open (see
 * `FilterItem.renderFilterControl`), so focusing on mount is equivalent to focusing on popover open.
 */
export function useFocusFirstActionableElement<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    focusInput(ref.current);
  }, []);

  return ref;
}

/**
 * @deprecated Listens for a `forge-popover-position` event that the pinned Forge version no longer
 * dispatches, so this is a no-op. Kept only for the class components in this directory that haven't
 * been converted to use {@link useFocusFirstActionableElement} yet — remove once they all have.
 */
export const addPopupListener = (
  popupRef: React.RefObject<any>,
  inputElement: HTMLDivElement,
  listenerRemovedCallback: () => void,
) => {
  const callback = () => {
    focusInput(inputElement);
    popupRef?.current?.removeEventListener('forge-popover-position', callback);
    listenerRemovedCallback();
  };

  popupRef?.current?.addEventListener('forge-popover-position', callback);
  return callback;
};

const focusInput = (element: HTMLElement | null) => {
  const actionableElements = element?.querySelectorAll('button, a, input, div[tabindex="0"]');
  const firstActionableElement = actionableElements?.[0] as HTMLElement | undefined;
  firstActionableElement?.focus();
};
